/**
 * FikoStepYazici — FikoStepMeshVerisi'ni ISO-10303-21 (STEP) metnine cevirir.
 *
 * Iki mod:
 *  - 'brep'        : FACETED_BREP (AP214 uyumlu). Her ucgen bir PLANE yuzeyi
 *                     olur. Kapali govdeler MANIFOLD_SOLID_BREP (gercek kati),
 *                     acik govdeler durustce SHELL_BASED_SURFACE_MODEL
 *                     (yuzey modeli, kati DEGIL) olarak yazilir — hicbir govde
 *                     olmadigi halde "kati" diye yalan soylenmez.
 *  - 'tessellated' : AP242 tarzi TRIANGULATED_SURFACE_SET. Topoloji/kapalilik
 *                     gerektirmez; acik/bozuk aglarda bile guvenlidir, dosya
 *                     boyutu B-Rep moduna gore genelde kucuktur.
 *
 * Bu dosya "yaprak" modul: sadece FikoStepGeometri'nin tiplerini/fonksiyonlarini
 * kullanir, hicbir FikoCizim* dosyasini import etmez.
 */
import type { FikoStepMeshVerisi } from './FikoStepGeometri';
import { fikoStepBilesenleriAyir } from './FikoStepGeometri';

export type FikoStepModu = 'brep' | 'tessellated';

export interface FikoStepYaziciSecenek {
  /** Urun/parca adi (dosya icindeki PRODUCT adi). */
  isim?: string;
  yazar?: string;
  organizasyon?: string;
}

export interface FikoStepSonuc {
  metin: string;
  /** brep modunda: en az bir govde kapali kati olarak mi yazildi. */
  kapaliGovdeSayisi: number;
  /** brep modunda: acik govde sayisi (SHELL_BASED_SURFACE_MODEL olarak yazilan). */
  acikGovdeSayisi: number;
  antetSayisi: number;
}

/* ================================================================== */
/* Dusuk seviye STEP yazim yardimcilari                                */
/* ================================================================== */

class StepYazar {
  private n = 0;
  private satirlar: string[] = [];

  /** Basit varlik: "#7=FOO('',...);" */
  yeni(govde: string): number {
    this.n++;
    this.satirlar.push(`#${this.n}=${govde};`);
    return this.n;
  }

  /** Karmasik (complex) varlik: "#7=(FOO(...)BAR(...));" */
  yeniKarmasik(govde: string): number {
    this.n++;
    this.satirlar.push(`#${this.n}=(${govde});`);
    return this.n;
  }

  get govdeMetni(): string { return this.satirlar.join('\r\n'); }
  get sayac(): number { return this.n; }
}

function sayiYaz(v: number): string {
  let x = v;
  if (!isFinite(x)) x = 0;
  if (Object.is(x, -0)) x = 0;
  let s = x.toFixed(6);
  s = s.replace(/0+$/, '');
  if (s.endsWith('.')) return s;
  return s;
}

function metinYaz(s: string): string {
  return "'" + (s || '').replace(/\\/g, '\\\\').replace(/'/g, "''") + "'";
}

function noktaEkle(y: StepYazar, x: number, yy: number, z: number): number {
  return y.yeni(`CARTESIAN_POINT('',(${sayiYaz(x)},${sayiYaz(yy)},${sayiYaz(z)}))`);
}
function yonEkle(y: StepYazar, x: number, yy: number, z: number): number {
  return y.yeni(`DIRECTION('',(${sayiYaz(x)},${sayiYaz(yy)},${sayiYaz(z)}))`);
}
function yerlesimEkle(y: StepYazar, merkez: number, eksenZ: number, eksenX: number): number {
  return y.yeni(`AXIS2_PLACEMENT_3D('',#${merkez},#${eksenZ},#${eksenX})`);
}

/* ================================================================== */
/* B-Rep (FACETED_BREP) uretimi                                        */
/* ================================================================== */

function brepGovdeleriUret(
  veri: FikoStepMeshVerisi,
  y: StepYazar
): { govdeler: number[]; kapaliGovdeSayisi: number; acikGovdeSayisi: number } {
  const konumlar = veri.konumlar;
  const ucgenler = veri.ucgenler;
  const noktaSayisi = veri.noktaSayisi;

  const bilesenler = fikoStepBilesenleriAyir(ucgenler, noktaSayisi);
  const noktaOnbellek = new Map<number, number>();

  const noktaAl = (i: number): number => {
    let id = noktaOnbellek.get(i);
    if (id !== undefined) return id;
    id = noktaEkle(y, konumlar[i * 3], konumlar[i * 3 + 1], konumlar[i * 3 + 2]);
    noktaOnbellek.set(i, id);
    return id;
  };

  const govdeler: number[] = [];
  let kapaliGovdeSayisi = 0;
  let acikGovdeSayisi = 0;

  for (const b of bilesenler) {
    const yuzler: number[] = [];

    for (const t of b.ucgenIndisleri) {
      const ia = ucgenler[t * 3], ib = ucgenler[t * 3 + 1], ic = ucgenler[t * 3 + 2];
      const ax = konumlar[ia * 3], ay = konumlar[ia * 3 + 1], az = konumlar[ia * 3 + 2];
      const bx = konumlar[ib * 3], by = konumlar[ib * 3 + 1], bz = konumlar[ib * 3 + 2];
      const cx = konumlar[ic * 3], cy = konumlar[ic * 3 + 1], cz = konumlar[ic * 3 + 2];

      const ux = bx - ax, uy = by - ay, uz = bz - az;
      const vx = cx - ax, vy = cy - ay, vz = cz - az;
      let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      const nUzun = Math.hypot(nx, ny, nz);
      if (nUzun < 1e-12) continue; // dejenere/sifir alan — atla
      nx /= nUzun; ny /= nUzun; nz /= nUzun;

      const rUzun = Math.hypot(ux, uy, uz) || 1;
      const rx = ux / rUzun, ry = uy / rUzun, rz = uz / rUzun;

      const p1 = noktaAl(ia), p2 = noktaAl(ib), p3 = noktaAl(ic);
      const merkez = noktaEkle(y, ax, ay, az);
      const eksenZ = yonEkle(y, nx, ny, nz);
      const eksenX = yonEkle(y, rx, ry, rz);
      const yerlesim = yerlesimEkle(y, merkez, eksenZ, eksenX);
      const duzlem = y.yeni(`PLANE('',#${yerlesim})`);
      const halka = y.yeni(`POLY_LOOP('',(#${p1},#${p2},#${p3}))`);
      const sinir = y.yeni(`FACE_OUTER_BOUND('',#${halka},.T.)`);
      const yuz = y.yeni(`FACE_SURFACE('',(#${sinir}),#${duzlem},.T.)`);
      yuzler.push(yuz);
    }

    if (yuzler.length === 0) continue;

    const kapaliGovde = b.sinirKenar === 0 && b.manifoldDisiKenar === 0;
    if (kapaliGovde) {
      const kabuk = y.yeni(`CLOSED_SHELL('',(${yuzler.map(i => '#' + i).join(',')}))`);
      const kati = y.yeni(`MANIFOLD_SOLID_BREP('',#${kabuk})`);
      govdeler.push(kati);
      kapaliGovdeSayisi++;
    } else {
      // Durustce: govde kapali degilse "kati" demiyoruz, yuzey modeli yaziyoruz.
      const kabuk = y.yeni(`OPEN_SHELL('',(${yuzler.map(i => '#' + i).join(',')}))`);
      const yuzeyModeli = y.yeni(`SHELL_BASED_SURFACE_MODEL('',(#${kabuk}))`);
      govdeler.push(yuzeyModeli);
      acikGovdeSayisi++;
    }
  }

  return { govdeler, kapaliGovdeSayisi, acikGovdeSayisi };
}

/* ================================================================== */
/* AP242 Tessellated (TRIANGULATED_SURFACE_SET) uretimi                */
/* ================================================================== */

function tessellatedYuzeyUret(veri: FikoStepMeshVerisi, y: StepYazar): number {
  const konumlar = veri.konumlar;
  const ucgenler = veri.ucgenler;
  const noktaSayisi = veri.noktaSayisi;

  const noktaMetinleri: string[] = new Array(noktaSayisi);
  for (let i = 0; i < noktaSayisi; i++) {
    noktaMetinleri[i] = `(${sayiYaz(konumlar[i * 3])},${sayiYaz(konumlar[i * 3 + 1])},${sayiYaz(konumlar[i * 3 + 2])})`;
  }
  const koordinatlar = y.yeni(`COORDINATES_LIST('',(${noktaMetinleri.join(',')}))`);

  const ucgenSayisi = ucgenler.length / 3;
  const ucgenMetinleri: string[] = new Array(ucgenSayisi);
  for (let t = 0; t < ucgenSayisi; t++) {
    // STEP'te liste indisleri 1 tabanlidir.
    ucgenMetinleri[t] = `(${ucgenler[t * 3] + 1},${ucgenler[t * 3 + 1] + 1},${ucgenler[t * 3 + 2] + 1})`;
  }

  return y.yeni(
    `TRIANGULATED_SURFACE_SET('',#${koordinatlar},${noktaSayisi},(),(),(${ucgenMetinleri.join(',')}))`
  );
}

/* ================================================================== */
/* Ortak urun/baglam iskeleti + ust duzey giris noktasi                */
/* ================================================================== */

export function fikoStepMetniUret(
  veri: FikoStepMeshVerisi,
  mod: FikoStepModu,
  secenek: FikoStepYaziciSecenek = {}
): FikoStepSonuc {
  if (!veri || veri.ucgenSayisi === 0) throw new Error('STEP icin ucgen verisi yok.');

  const isim = secenek.isim || 'FikoYT_Model';
  const yazar = secenek.yazar || 'FikoYT';
  const organizasyon = secenek.organizasyon || 'FikoYT';
  const y = new StepYazar();

  // --- Birimler + geometrik baglam (mm, standart AP boilerplate) -----
  const uzunlukBirimi = y.yeniKarmasik(`LENGTH_UNIT()NAMED_UNIT(*)SI_UNIT(.MILLI.,.METRE.)`);
  const aciBirimi = y.yeniKarmasik(`NAMED_UNIT(*)PLANE_ANGLE_UNIT()SI_UNIT($,.RADIAN.)`);
  const katiAciBirimi = y.yeniKarmasik(`NAMED_UNIT(*)SI_UNIT($,.STERADIAN.)SOLID_ANGLE_UNIT()`);
  const belirsizlik = y.yeni(
    `UNCERTAINTY_MEASURE_WITH_UNIT(LENGTH_MEASURE(1.E-4),#${uzunlukBirimi},'distance_accuracy_value','FikoYT dogruluk degeri')`
  );
  const baglam = y.yeniKarmasik(
    `GEOMETRIC_REPRESENTATION_CONTEXT(3)GLOBAL_UNCERTAINTY_ASSIGNED_CONTEXT((#${belirsizlik}))GLOBAL_UNIT_ASSIGNED_CONTEXT((#${uzunlukBirimi},#${aciBirimi},#${katiAciBirimi}))REPRESENTATION_CONTEXT('FikoYT Baglam','3D')`
  );

  // --- Urun tanimi ------------------------------------------------------
  const appBaglam = y.yeni(`APPLICATION_CONTEXT('automotive design')`);
  y.yeni(`APPLICATION_PROTOCOL_DEFINITION('international standard','automotive_design',2003,#${appBaglam})`);
  const urunBaglam = y.yeni(`PRODUCT_CONTEXT('',#${appBaglam},'mechanical')`);
  const urun = y.yeni(`PRODUCT(${metinYaz(isim)},${metinYaz(isim)},'',(#${urunBaglam}))`);
  y.yeni(`PRODUCT_RELATED_PRODUCT_CATEGORY('part',$,(#${urun}))`);
  const urunOlusum = y.yeni(`PRODUCT_DEFINITION_FORMATION('','',#${urun})`);
  const tanimBaglam = y.yeni(`PRODUCT_DEFINITION_CONTEXT('part definition',#${appBaglam},'design')`);
  const tanim = y.yeni(`PRODUCT_DEFINITION('design','',#${urunOlusum},#${tanimBaglam})`);
  const sekil = y.yeni(`PRODUCT_DEFINITION_SHAPE('','',#${tanim})`);

  // --- Kok yerlesim (dunya ekseni) — her iki modda da kullanilir --------
  const kokNokta = noktaEkle(y, 0, 0, 0);
  const kokZ = yonEkle(y, 0, 0, 1);
  const kokX = yonEkle(y, 1, 0, 0);
  const kokYerlesim = yerlesimEkle(y, kokNokta, kokZ, kokX);

  let kapaliGovdeSayisi = 0;
  let acikGovdeSayisi = 0;
  let temsilId: number;

  if (mod === 'brep') {
    const sonuc = brepGovdeleriUret(veri, y);
    kapaliGovdeSayisi = sonuc.kapaliGovdeSayisi;
    acikGovdeSayisi = sonuc.acikGovdeSayisi;

    if (sonuc.govdeler.length === 0) {
      throw new Error('STEP B-Rep: yazilacak gecerli yuz bulunamadi.');
    }

    const ogeler = [kokYerlesim, ...sonuc.govdeler].map(i => '#' + i).join(',');
    if (kapaliGovdeSayisi > 0 && acikGovdeSayisi === 0 && sonuc.govdeler.length === 1) {
      temsilId = y.yeni(`ADVANCED_BREP_SHAPE_REPRESENTATION('',(${ogeler}),#${baglam})`);
    } else {
      temsilId = y.yeni(`SHAPE_REPRESENTATION('',(${ogeler}),#${baglam})`);
    }
  } else {
    const yuzeySeti = tessellatedYuzeyUret(veri, y);
    const ogeler = `#${kokYerlesim},#${yuzeySeti}`;
    temsilId = y.yeni(`SHAPE_REPRESENTATION('',(${ogeler}),#${baglam})`);
  }

  y.yeni(`SHAPE_DEFINITION_REPRESENTATION(#${sekil},#${temsilId})`);

  // --- Dosyayi birlestir --------------------------------------------
  const suan = new Date().toISOString().replace(/\.\d+Z$/, '');
  const sema = mod === 'brep'
    ? "'AUTOMOTIVE_DESIGN { 1 0 10303 214 3 1 1 }'"
    : "'AP242_MANAGED_MODEL_BASED_3D_ENGINEERING_MIM_LF { 1 0 10303 442 3 1 4 }'";

  const baslik = [
    'ISO-10303-21;',
    'HEADER;',
    `FILE_DESCRIPTION((''),'2;1');`,
    `FILE_NAME(${metinYaz(isim + '.step')},'${suan}',(${metinYaz(yazar)}),(${metinYaz(organizasyon)}),'FikoYT STEP Yazici 1.0','FikoYT','');`,
    `FILE_SCHEMA((${sema}));`,
    'ENDSEC;',
    'DATA;'
  ].join('\r\n');

  const govde = y.govdeMetni;

  const altbilgi = ['ENDSEC;', 'END-ISO-10303-21;'].join('\r\n');

  const metin = `${baslik}\r\n${govde}\r\n${altbilgi}\r\n`;

  return { metin, kapaliGovdeSayisi, acikGovdeSayisi, antetSayisi: y.sayac };
}
